import React, { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { REFRESH_TOKEN_SENTIMENT } from '../graphql/queries/tokenSentiment';
import { formatRelativeTime } from '../utils/dateUtils';

function SentimentScore({ score, description, sources }) {
  // Determine color based on sentiment score (0-10)
  let color = 'bg-gray-200'; // Default neutral
  let textColor = 'text-gray-800';
  
  if (score >= 7.5) {
    color = 'bg-green-500'; 
    textColor = 'text-white';
  } else if (score >= 6) {
    color = 'bg-green-400';
    textColor = 'text-white';
  } else if (score >= 5) {
    color = 'bg-blue-400';
    textColor = 'text-white';
  } else if (score >= 4) {
    color = 'bg-yellow-400';
    textColor = 'text-gray-800';
  } else if (score >= 2.5) {
    color = 'bg-orange-400';
    textColor = 'text-white';
  } else {
    color = 'bg-red-500';
    textColor = 'text-white';
  }
  
  return (
    <div className="flex items-start">
      <div className={`${color} rounded-full w-14 h-14 flex items-center justify-center ${textColor} font-bold text-xl mr-4 shrink-0`}>
        {score.toFixed(1)}
      </div>
      <div>
        <p className="text-gray-700 dark:text-gray-300">{description}</p>
        <div className="flex flex-wrap gap-2 mt-2">
          {sources && sources.length > 0 && sources.map((source, index) => (
            <a
              key={index}
              href={source.url && source.url.startsWith('http') ? source.url : '#'}
              target="_blank"
              rel="noopener noreferrer"
              title={source.url}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <span>{source.title || 'Source'}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

const TokenSentiment = ({ ticker, tokenId, sentimentHistory: initialSentimentHistory = [] }) => {
  const [sentimentHistory, setSentimentHistory] = useState(initialSentimentHistory);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Initialize from provided history if available
  useEffect(() => {
    if (initialSentimentHistory && initialSentimentHistory.length > 0) {
      setSentimentHistory(initialSentimentHistory);
      setHistoryIndex(0);
    }
  }, [initialSentimentHistory]);
  
  // Query to refresh sentiment when needed - only used when refresh button is clicked
  const [getSentiment, { loading: sentimentLoading }] = useLazyQuery(REFRESH_TOKEN_SENTIMENT, {
    fetchPolicy: 'network-only', // Don't use cache for this query
    onCompleted: (data) => {
      console.log('Sentiment data received:', data);
      if (data?.tokenSentiment) {
        // Add the new sentiment at the beginning of history and update index
        setSentimentHistory(prev => [data.tokenSentiment, ...prev]);
        setHistoryIndex(0); // Reset to show the newest
      }
    },
    onError: (error) => {
      console.error('Error fetching sentiment:', error);
    }
  });

  // Function to refresh token sentiment - only called when refresh button is clicked
  const refreshSentiment = async () => {
    if (!ticker) {
      console.error('Cannot refresh sentiment: ticker is missing');
      return;
    }
    
    setIsRefreshing(true);
    try {
      console.log('Refreshing sentiment for:', ticker);
      const result = await getSentiment({ 
        variables: { 
          ticker: ticker,
          forceRefresh: true
        }
      });
      console.log('Refresh sentiment full response:', result);
      
      // Check if we got any data back
      if (result.data && result.data.tokenSentiment) {
        console.log('Successfully fetched sentiment:', result.data.tokenSentiment);
      } else {
        console.warn('No sentiment data returned from API:', result);
      }
    } catch (err) {
      console.error("Error refreshing sentiment:", err);
    } finally {
      setIsRefreshing(false);
    }
  };
  
  // Navigate through sentiment history
  const handleHistoryNavigation = (direction) => {
    if (sentimentHistory.length <= 1) return;
    
    switch (direction) {
      case 'newest':
        setHistoryIndex(0);
        break;
      case 'oldest':
        setHistoryIndex(sentimentHistory.length - 1);
        break;
      case 'next':
        if (historyIndex > 0) {
          setHistoryIndex(historyIndex - 1);
        }
        break;
      case 'prev':
        if (historyIndex < sentimentHistory.length - 1) {
          setHistoryIndex(historyIndex + 1);
        }
        break;
      default:
        break;
    }
  };
  
  const isLoading = sentimentLoading || isRefreshing;
  const displaySentiment = sentimentHistory[historyIndex];

  if (isLoading && sentimentHistory.length === 0) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600">
            Analyzing sentiment...
          </span>
        </div>
      </div>
    );
  }

  if (!displaySentiment) {
    return (
      <div className="p-4">
        <div className="flex flex-col gap-4">
          <div className="flex justify-center">
            <button 
              onClick={refreshSentiment}
              className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Get Sentiment'}
            </button>
          </div>
          <div className="text-center py-2">
            <p className="text-gray-500 italic mb-2">No sentiment data available for {ticker}.</p>
            {tokenId ? (
              <p className="text-sm text-gray-400">Click the button above to analyze market sentiment.</p>
            ) : (
              <p className="text-sm text-gray-400">This token needs to be added to your portfolio first.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Format relative timestamp
  const relativeTime = formatRelativeTime(displaySentiment.timestamp);

  return (
    <div className="p-4">
      <div className="mb-2 flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <SentimentScore score={displaySentiment.score} description={displaySentiment.description} sources={displaySentiment.sources} />
      </div>
      
      <div className="flex flex-col md:flex-row gap-4 mt-4">
        <div className="md:w-2/3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
              <h4 className="font-medium text-green-700 dark:text-green-400 mb-2">Positive Factors</h4>
              <ul className="list-disc pl-5 text-sm">
                {displaySentiment.pros.map((pro, index) => (
                  <li key={index} className="mb-1">{pro}</li>
                ))}
              </ul>
            </div>
            
            <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
              <h4 className="font-medium text-red-700 dark:text-red-400 mb-2">Negative Factors</h4>
              <ul className="list-disc pl-5 text-sm">
                {displaySentiment.cons.map((con, index) => (
                  <li key={index} className="mb-1">{con}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        
        <div className="md:w-1/3 flex flex-col justify-start">
          <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg h-full relative">
            {/* History navigation in top-right of this panel */}
            {sentimentHistory.length > 1 && (
              <div className="absolute top-2 right-2 flex items-center space-x-1 bg-white dark:bg-gray-800 rounded-md px-1 py-0.5 shadow-sm">
                <button 
                  onClick={() => handleHistoryNavigation('prev')}
                  disabled={historyIndex === sentimentHistory.length - 1}
                  className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Previous sentiment"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                <span className="text-xs font-medium">
                  {historyIndex + 1}/{sentimentHistory.length}
                </span>
                <button 
                  onClick={() => handleHistoryNavigation('next')}
                  disabled={historyIndex === 0}
                  className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Next sentiment"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            )}
            <div className="flex flex-col h-full justify-between">
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Sentiment Analysis</h4>
                <p className="text-xs text-gray-500 mb-4">
                  Updated {relativeTime}
                </p>
              </div>
              <button 
                onClick={refreshSentiment}
                className="w-full px-3 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
                disabled={isLoading}
              >
                {isLoading ? 'Refreshing...' : 'Refresh Sentiment'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenSentiment; 